// MyDevices – DataGrid-based refactor
// -----------------------------------------------------------------------------
// Este componente reemplaza la tabla manual por MUI DataGrid. Mantiene:
// - Queries y Mutations de Apollo
// - Modales de Comentarios y Calibración
// - Descarga de QRs seleccionados
// - Edición inline para Admin y cambios rápidos para Manager (level === 2)
// Tips de lectura:
// 1) "State & Derived data" -> estados locales y memo de filas
// 2) "Queries/Mutations" -> integración con GraphQL
// 3) "Renderers" -> celdas personalizadas (battery, status, etc.)
// 4) "Columns" -> definición dinámica por rol (admin/company)
// 5) "Handlers" -> edición, guardado, selección, QRs, modales
// 6) "DataGrid" -> configuración (autoHeight, paginación, toolbar, selección)

import React, { useState, useEffect, useMemo } from "react";
import { FormattedMessage } from "react-intl";
import styles from "./MyDevices.module.scss";
import { DataGrid, GridToolbarQuickFilter } from "@mui/x-data-grid";
import { AiOutlineDownload } from "react-icons/ai";
import { useQuery, useMutation, useLazyQuery } from "@apollo/client";
import {
  GET_DEVICES,
  GET_COMPANIES,
  UPDATE_DEVICE,
  DELETE_DEVICE,
  GET_SHIPMENTS_PAG2,
} from "../../../queries";

import { devicesList, devicesTempRanges, devicesSensors } from "../../../const";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import DevicesComments from "../DevicesComments/DevicesComments";
import { CircularProgress, Tooltip } from "@mui/material";
import { withStyles } from "@material-ui/core/styles";
import jsPDF from "jspdf";
import QRCode from "qrcode";
import CustomButton from "../../Buttons/CustomButton";
import DeviceCalibrationModal from "./deviceCalibrationModal/DeviceCalibrationModal";
import { ToastContainer } from "react-toastify";
import { fetchCalibrationReport } from "../../../helpers";

// --- Styles for Comments/Calibration modals -------------------------------------------------
const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  height: 300,
  bgcolor: "white",
  border: "3px solid #00ABC8",
  borderRadius: 10,
  fontFamily: "'Quattrocento Sans', sans-serif",
  boxShadow: 24,
  pt: 3,
  px: 4,
  pb: 4,
};

export default function MyDevices({ company_id, level }) {
  const CustomTooltip = withStyles(() => ({
    tooltip: { fontSize: "18px" },
  }))(Tooltip);

  // --- Local state --------------------------------------------------------------------------
  // page/pageSize: controlan paginación de DataGrid (0-indexed)
  // editDeviceId: id del device que está en modo edición (admin)
  // actualDeviceData: buffer de cambios para mutation UPDATE_DEVICE
  // commentsOpen/devToSeeComments: control del modal de comentarios
  // calibrationModal/lastCalibrationInfo/deviceToCalibrate: control del modal de calibración
  // selectionModel: filas seleccionadas para descargas de QR

  const [page, setPage] = useState(0); // DataGrid is 0-indexed
  const [pageSize, setPageSize] = useState(10);

  const [editDeviceId, setEditDeviceId] = useState(null);
  const [actualDeviceData, setActualDeviceData] = useState({});

  const [commentsOpen, setCommentsOpen] = useState(false);
  const [devToSeeComments, setDevToSeeComments] = useState("");

  const [calibrationModal, setCalibrationModal] = useState(false);
  const [lastCalibrationInfo, setLastCalibrationInfo] = useState(false);
  const [deviceToCalibrate, setDeviceToCalibrate] = useState({});

  const [selectionModel, setSelectionModel] = useState([]); // selected row ids (device_id)

  // --- Queries / Mutations ------------------------------------------------------------------
  // GET_SHIPMENTS_PAG2: sólo se ejecuta con company_id (shipments en tránsito)
  // GET_COMPANIES: catálogo de compañías (admin)
  // GET_DEVICES: listado de dispositivos, fetchPolicy network-only para mantenerlo fresco
  // UPDATE_DEVICE/DELETE_DEVICE: acciones sobre cada dispositivo

  const { data: transitData } = useQuery(GET_SHIPMENTS_PAG2, {
    variables: { in_transit: true, company_id, per_page: 500, page: 1 },
    skip: !company_id, // only relevant when company scope
  });
  const ships_in_transit = transitData?.shipments?.selectedItems || [];

  const [getAllCompanies, { data: allCompaniesData, error: allCompaniesError }] =
    useLazyQuery(GET_COMPANIES);

  const [updateDevice, { loading: updateDeviceLoading, error: updateDeviceError }] =
    useMutation(UPDATE_DEVICE);

  const [getDevices, { error: devicesError, data: devicesData }] =
    useLazyQuery(GET_DEVICES, { fetchPolicy: "network-only" });

  const [deleteDevice, { error: deleteDeviceError }] = useMutation(DELETE_DEVICE);

  // --- Effects ------------------------------------------------------------------------------
  useEffect(() => {
    if (!company_id) getAllCompanies();
    getDevices({ variables: { assigned_to: company_id ? company_id : "" } });
  }, [company_id, getAllCompanies, getDevices]);

  // --- Derived data -------------------------------------------------------------------------
  // rows: a partir de devices -> map a { id, ...d }
  // companies: catálogo de compañías para dropdown de asignación

  const rows = useMemo(() => {
    const list = devicesData?.devices || [];
    return list.map((d) => ({ id: d.device_id, ...d }));
  }, [devicesData]);

  const companies = allCompaniesData?.companies || [];

  // --- Handlers -----------------------------------------------------------------------------
  // startEdit/cancelEdit/saveEdit: flujo de edición admin (modo fila)
  // onFieldChange: maneja updates campo a campo (incluye quick-saves para rango y status)
  // removeDevice: elimina y refetch de devices
  // open/closeComments: abre/cierra modal de comentarios
  // open/closeCalibration & downloadCalibration: modal + descarga de último PDF
  // printQRCode: genera PDF con QRs de filas seleccionadas (selectionModel)

  const startEdit = (row) => {
    setEditDeviceId(row.device_id);
    setActualDeviceData({ device_id: row.device_id, sensors: row.sensors || [] });
  };
  const cancelEdit = () => {
    setEditDeviceId(null);
    setActualDeviceData({});
  };

  const saveTempRangeQuick = async (device_id, temperature_range) => {
    try {
      await updateDevice({
        variables: { device_id, temperature_range },
        refetchQueries: [{ query: GET_DEVICES, variables: { assigned_to: company_id || "" } }],
      });
    } catch (e) {
      // no-op, errors are logged below
    }
  };

  const saveStatusQuick = async (device_id, status) => {
    try {
      await updateDevice({
        variables: { device_id, status },
        refetchQueries: [{ query: GET_DEVICES, variables: { assigned_to: company_id || "" } }],
      });
    } catch (e) {
      // no-op
    }
  };

  const onFieldChange = async (e, row) => {
    const { name, value, checked, type } = e.target;
    if (name === "sensors") {
      setActualDeviceData((prev) => ({
        ...prev,
        sensors: checked ? [...(prev.sensors || []), value] : (prev.sensors || []).filter((s) => s !== value),
      }));
      return;
    }
    if (name === "assigned_to") {
      setActualDeviceData((prev) => ({ ...prev, assigned_to: value }));
      return;
    }
    if (name === "was_serviced") {
      setActualDeviceData((prev) => ({ ...prev, was_serviced: checked }));
      return;
    }
    if (name === "battery_level") {
      setActualDeviceData((prev) => ({ ...prev, battery_level: parseFloat(value) }));
      return;
    }
    if (name === "temperature_range") {
      // quick-save path for L2 or row editing temp range
      await saveTempRangeQuick(row.device_id, value);
      return;
    }
    if (name === "status") {
      await saveStatusQuick(row.device_id, value);
      return;
    }
    setActualDeviceData((prev) => ({ ...prev, [name]: type === "number" ? Number(value) : value }));
  };

  const saveEdit = async () => {
    if (!actualDeviceData?.sensors?.length) {
      alert(
        localStorage.getItem("language") === "en"
          ? "Please mark at least one sensor type"
          : "Por favor marca al menos un tipo de sensor"
      );
      return;
    }
    try {
      if (
        window.confirm(
          localStorage.getItem("language") === "es"
            ? "¿Estás seguro que quieres guardar cambios?"
            : "Are you sure you want to confirm changes?"
        )
      ) {
        await updateDevice({ variables: actualDeviceData });
        await getDevices({ variables: { assigned_to: company_id ? company_id : "" } });
        setEditDeviceId(null);
      }
    } catch (error) {
      // handled by error logs
    }
  };

  const removeDevice = async (id) => {
    try {
      if (
        window.confirm(
          localStorage.getItem("language") === "es"
            ? "¿Estás seguro que deseas eliminar el dispositivo?"
            : "Are you sure you want to delete device?"
        )
      ) {
        await deleteDevice({ variables: { device_id: id } });
        await getDevices({ variables: { assigned_to: company_id ? company_id : "" } });
      }
    } catch (error) { }
  };

  const openComments = (id) => {
    setCommentsOpen(true);
    setDevToSeeComments(id);
  };

  const closeComments = () => setCommentsOpen(false);

  const openCalibration = (device) => {
    setCalibrationModal(true);
    setDeviceToCalibrate(device);
  };
  const closeCalibration = () => {
    setCalibrationModal(false);
    setLastCalibrationInfo(false);
  };

  const downloadCalibration = async (device) => {
    await fetchCalibrationReport(device);
    setLastCalibrationInfo(true);
  };

  // --- QR PDF -------------------------------------------------------------------------------
  // Usa jsPDF y qrcode para renderizar QRs en un documento A4 con grilla, agregando el texto (QR) debajo.

  const printQRCode = async () => {
    const pdf = new jsPDF();
    const qrSize = 50;
    const padding = 10;
    const extraHeight = 20;
    const qrPerRow = Math.floor((pdf.internal.pageSize.getWidth() - padding) / (qrSize + padding));

    const selectedQRs = rows
      .filter((r) => selectionModel.includes(r.device_id || r.id))
      .map((r) => r.qr)
      .filter(Boolean);

    const drawQR = async (qr, x, y) => {
      const qrCanvas = document.createElement("canvas");
      await QRCode.toCanvas(qrCanvas, qr, { width: qrSize, height: qrSize });
      const qrDataUrl = qrCanvas.toDataURL("image/png");
      pdf.addImage(qrDataUrl, "PNG", x, y, qrSize, qrSize);
      pdf.setFontSize(30);
      pdf.text(qr, x + qrSize / 2, y + qrSize + padding, { align: "center" });
    };

    for (let i = 0; i < selectedQRs.length; i++) {
      const qr = selectedQRs[i];
      const x = padding + (i % qrPerRow) * (qrSize + padding);
      const y = padding + Math.floor(i / qrPerRow) * (qrSize + extraHeight + padding);
      if (y + qrSize + extraHeight + padding > pdf.internal.pageSize.getHeight()) pdf.addPage();
      // eslint-disable-next-line no-await-in-loop
      await drawQR(qr, x, y);
    }

    pdf.save("qrs.pdf");
  };

  // --- Renderers (celdas) -------------------------------------------------------------------
  // batteryRenderer: muestra porcentaje con color (rojo <=15%, verde resto). En edición, input number.
  const batteryRenderer = (params) => {
    const val = params.row?.battery_level;
    const color = parseInt(val, 10) <= 15 ? "#D60707" : "#33B27F";
    if (editDeviceId === params.row.device_id) {
      return (
        <input
          type="number"
          defaultValue={val}
          name="battery_level"
          max="100"
          min="0"
          onChange={(e) => onFieldChange(e, params.row)}
          style={{ border: "none", borderBottom: "2px solid grey", width: 70, textAlign: "center", background: "transparent" }}
        />
      );
    }
    return <span style={{ color }}>{val ? `${val}%` : "--"}</span>;
  };

  // tempRangeRenderer: label simple o <select> compacto para admin en edición o manager (level===2). Quick-save.
  const tempRangeRenderer = (params) => {
    const row = params.row;
    const label = row?.temperature_range?.name || row?.temperature_range || "--";
    const canEditInline = editDeviceId === row.device_id || level === 2;
    if (!canEditInline) return label;
    return (
      <select
        onChange={(e) => onFieldChange(e, row)}
        defaultValue="default select"
        name="temperature_range"
        className={styles.select}
        style={{ minWidth: 120, maxWidth: 160, fontSize: "0.85rem", padding: "2px 6px", height: 32, textAlign: "center" }}
      >
        <option disabled hidden value="default select">{label || "Select a temperature range"}</option>
        {devicesTempRanges?.map((t) => (
          <option value={t} key={t}>{t}</option>
        ))}
      </select>
    );
  };

  // formatRenderer: dropdown de formatos en edición; texto simple si no.
  const formatRenderer = (params) => {
    const row = params.row;
    if (editDeviceId === row.device_id) {
      return (
        <select onChange={(e) => onFieldChange(e, row)} defaultValue="default select" name="device_format" className={styles.select}>
          <option disabled hidden value="default select">{row.device_format}</option>
          {devicesList?.map((d) => (
            <option value={devicesList?.indexOf(d)} key={d}>{d}</option>
          ))}
        </select>
      );
    }
    return row.device_format || "--";
  };

  // sensorsRenderer: en edición muestra checkboxes; si no, lista estática de sensores.
  const sensorsRenderer = (params) => {
    const row = params.row;
    if (editDeviceId === row.device_id) {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {devicesSensors.map((s) => (
            <label key={s} className={styles.sensorsInputContainer}>
              <input
                type="checkbox"
                value={s}
                name="sensors"
                onChange={(e) => onFieldChange(e, row)}
                defaultChecked={row?.sensors?.includes(s)}
              />
              {s}
            </label>
          ))}
        </div>
      );
    }
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {(row?.sensors || []).map((s) => (
          <div key={s} className={styles.sensorsInputContainer}>{s}</div>
        ))}
      </div>
    );
  };

  // assignedRenderer: dropdown de compañías en edición (admin); texto simple si no.
  const assignedRenderer = (params) => {
    const row = params.row;
    if (editDeviceId === row.device_id) {
      return (
        <select onChange={(e) => onFieldChange(e, row)} defaultValue="default select" name="assigned_to" className={styles.select}>
          <option disabled hidden value="default select">{row.assigned_to || "Select a company"}</option>
          {companies.map((c) => (
            <option value={c.company_id} key={c.company_id}>{c.company_id}</option>
          ))}
        </select>
      );
    }
    return row.assigned_to || "-";
  };

  // statusRenderer: visible para ambos perfiles; editable si admin en edición o manager (level===2). Quick-save.
  const statusRenderer = (params) => {
    const row = params.row;
    const canEdit = editDeviceId === row.device_id || level === 2; // admin editing or manager quick change
    if (canEdit) {
      return (
        <select
          onChange={(e) => onFieldChange(e, row)}
          value={row.status || "INACTIVE"}
          name="status"
          className={styles.select}
          style={{ minWidth: 120, maxWidth: 160, fontSize: "0.85rem", padding: "2px 6px", height: 32, textAlign: "center" }}
        >
          <option value="ACTIVE">{localStorage.getItem("language") === "en" ? "Active" : "Activo"}</option>
          <option value="INACTIVE">{localStorage.getItem("language") === "en" ? "Inactive" : "Inactivo"}</option>
        </select>
      );
    }
    return (
      <>
        {row.status === "ACTIVE" && <FormattedMessage id="active">Active</FormattedMessage>}
        {row.status === "INACTIVE" && <FormattedMessage id="inactive">Inactive</FormattedMessage>}
      </>
    );
  };

  // qrRenderer: texto con ellipsis; en edición, input de texto.
  const qrRenderer = (params) => {
    const row = params.row;
    if (editDeviceId === row.device_id) {
      return (
        <input
          type="text"
          defaultValue={row.qr}
          name="qr"
          onChange={(e) => onFieldChange(e, row)}
          style={{ border: "none", borderBottom: "2px solid grey", width: 90, textAlign: "center", background: "transparent" }}
        />
      );
    }
    return (
      <div style={{ maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {row.qr}
      </div>
    );
  };

  // lastUseRenderer: fecha informada (YYYY-MM-DD hh:mm). Muestra checkbox "Was serviced" si está en edición.
  const lastUseRenderer = (params) => {
    const row = params.row;
    const val = row.last_use
      ? `${row.last_use.split("T")[0]} ${row.last_use.split("T")[1].split(":")[0]}:${row.last_use.split("T")[1].split(":")[1]}`
      : "-";
    return (
      <div>
        {val}
        {editDeviceId === row.device_id && (
          <label style={{ marginLeft: 8 }}>
            <input name="was_serviced" type="checkbox" defaultChecked={false} onChange={(e) => onFieldChange(e, row)} />
            <FormattedMessage id="was_serviced">Was serviced</FormattedMessage>
          </label>
        )}
      </div>
    );
  };

  // shipmentsRenderer: busca shipment en tránsito por QR y muestra #<id-corto>; '--' si no hay.
  const shipmentsRenderer = (params) => {
    const row = params.row;
    const found = ships_in_transit.find((ship) => ship?.qr === row?.qr);
    return found ? `#${found.shipment_id.split("-")[1]}` : "--";
  };

  // actionsRenderer:
  // - Admin: botones Edit/Save/Cancel + Delete
  // - Company: botón Calibrate + descarga de último PDF si hay calibraciones
  const actionsRenderer = (params) => {
    const row = params.row;
    const isEditing = editDeviceId === row.device_id;
    if (!company_id) {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "center" }}>
          {!isEditing ? (
            <>
              <button onClick={() => startEdit(row)} className={styles.editDevicesButton}>
                <FormattedMessage id="edit_device_button">Edit</FormattedMessage>
              </button>
              <button onClick={() => removeDevice(row.device_id)} className={styles.deleteDevicesButton}>
                <FormattedMessage id="delete_device_button">Delete</FormattedMessage>
              </button>
            </>
          ) : (
            <>
              <button onClick={saveEdit} className={styles.editDevicesButton}>
                <FormattedMessage id="save_button">Save</FormattedMessage>
              </button>
              <button onClick={cancelEdit} className={styles.deleteDevicesButton}>
                <FormattedMessage id="cancel_button">Cancel</FormattedMessage>
              </button>
            </>
          )}
        </div>
      );
    }
    // company context: center vertically/horizontally and avoid horizontal scroll
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '28px 1fr 28px', // left spacer, center, right icon
          alignItems: 'center',
          width: '100%',
          height: '100%',
          overflow: 'hidden', // prevent cell from forcing grid horizontal scroll
        }}
      >
        {/* Left spacer (keeps center truly centered without growing width) */}
        <div style={{ width: 28, height: 28 }} />

        {/* Center action: Calibrate (bounded by cell, no minWidth, no overflow) */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'hidden', maxWidth: '100%' }}>
          <CustomButton
            type={'calibrate'}
            calibrateDeviceParams={{
              text: <FormattedMessage id={'calibrate'}>calibrate</FormattedMessage>,
              action: () => openCalibration(row),
            }}
            // ensure the button itself doesn't expand the cell
            style={{ maxWidth: '100%' }}
          />
        </div>

        {/* Right action: Download (fixed 28px slot). If not present, keep empty slot. */}
        {row?.calibrations?.length > 0 ? (
          <CustomTooltip title={<FormattedMessage id="descaga_pdf_cal" />}>
            <button
              onClick={() => downloadCalibration(row)}
              className={styles.qrDownloadButton}
              style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <AiOutlineDownload size="1rem" color="#1B1464" />
            </button>
          </CustomTooltip>
        ) : (
          <div style={{ width: 28, height: 28 }} />
        )}
      </div>
    );
  };

  const commentsRenderer = (params) => (
    <button className={styles.contentButton} onClick={() => openComments(params.row.device_id)}>
      <FormattedMessage id="content_button">OPEN</FormattedMessage>
    </button>
  );

  // --- Columns ------------------------------------------------------------------------------
  // Definición de columnas para DataGrid. Se arma dinámicamente según el contexto:
  // - Admin (sin company_id): columnas extra (ID corto, Assigned, Sensors, Version, Comments, acciones de edición)
  // - Company: columnas de shipment asociado y acciones de calibración/descarga
  // Notas:
  // - La columna QR ahora tiene width fija (120) y aplica ellipsis
  // - Status se muestra para ambos perfiles y es editable si admin está editando la fila o si es manager (level===2)

  const columns = useMemo(() => {
    const base = [];

    if (!company_id) {
      base.push({
        field: "idShort", headerName: "ID", width: 90, align: "center", headerAlign: "center",
        valueGetter: (params) => (params.row.device_id || "-").split("-")[1]
      });
    }

    // QR download en header (usa selección de filas)
    base.push({
      field: "qrPick", headerName: "", width: 60, sortable: false, filterable: false, disableColumnMenu: true,
      renderHeader: () => (
        <CustomTooltip title={<FormattedMessage id="descaga_qrs" />}>
          <span>
            <button onClick={printQRCode} className={styles.qrDownloadButton} disabled={selectionModel.length === 0}>
              <AiOutlineDownload size="1.2rem" color={selectionModel.length ? "#1B1464" : "#999"} />
            </button>
          </span>
        </CustomTooltip>
      ),
      renderCell: () => null,
    });

    base.push({ field: "qr", headerName: "QR", width: 120, align: "center", headerAlign: "center", renderCell: qrRenderer });
    base.push({ field: "battery_level", headerName: "Battery", width: 120, align: "center", headerAlign: "center", renderCell: batteryRenderer, sortable: true });

    if (!company_id) {
      base.push({ field: "assigned_to", headerName: "Assigned to", minWidth: 160, flex: 1, align: "center", headerAlign: "center", renderCell: assignedRenderer });
    }

    base.push({ field: "last_use", headerName: "Last use", minWidth: 170, align: "center", headerAlign: "center", renderCell: lastUseRenderer });
    base.push({ field: "last_location", headerName: "Last location", minWidth: 160, flex: 1, align: "center", headerAlign: "center" });
    base.push({ field: "temperature_range", headerName: "Type", minWidth: 180, align: "center", headerAlign: "center", renderCell: tempRangeRenderer });
    base.push({ field: "device_format", headerName: "Format", minWidth: 140, align: "center", headerAlign: "center", renderCell: formatRenderer });

    // Status visible para ambos perfiles
    base.push({ field: "status", headerName: "Status", width: 130, align: "center", headerAlign: "center", renderCell: statusRenderer });

    if (company_id) {
      base.push({ field: "ship", headerName: "Associated shipment", width: 180, align: "center", headerAlign: "center", renderCell: shipmentsRenderer });
      base.push({ field: "actions_company", headerName: "Actions", width: 160, align: "center", headerAlign: "center", sortable: false, filterable: false, renderCell: actionsRenderer });
    } else {
      base.push({ field: "sensors", headerName: "Sensors", minWidth: 200, flex: 1, align: "center", headerAlign: "center", renderCell: sensorsRenderer });
      base.push({
        field: "version", headerName: "Version", width: 120, align: "center", headerAlign: "center",
        renderCell: (params) => (
          editDeviceId === params.row.device_id ? (
            <input type="text" defaultValue={params.row.version} name="version" onChange={(e) => onFieldChange(e, params.row)}
              style={{ border: "none", borderBottom: "2px solid grey", width: 90, textAlign: "center", background: "transparent" }} />
          ) : (
            params.row.version
          )
        )
      });
      base.push({ field: "comments", headerName: "Comments", width: 140, align: "center", headerAlign: "center", renderCell: commentsRenderer, sortable: false, filterable: false });
      base.push({ field: "actions_admin", headerName: "Accions", width: 150, align: "center", headerAlign: "center", renderCell: actionsRenderer, sortable: false, filterable: false });
    }

    // ⬅️ Importante: devolver SIEMPRE un array
    return base;
  }, [company_id, selectionModel, level, editDeviceId, ships_in_transit, companies]);

  // --- Errors logging -----------------------------------------------------------------------
  if (devicesError) console.log("error en el get devices", devicesError);
  if (allCompaniesError) console.log("error en el get all companies", allCompaniesError);
  if (updateDeviceError) console.log("error en el update de devices", updateDeviceError);
  if (deleteDeviceError) console.log("error en el delete del device", deleteDeviceError);

  if (updateDeviceLoading) {
    return (
      <div>
        <CircularProgress />
      </div>
    );
  }

  // --- Render principal ---------------------------------------------------------------------
  // DataGrid:
  // - autoHeight: evita scroll interno, deja que el contenedor crezca con las filas
  // - checkboxSelection: permite seleccionar filas para descargar QRs
  // - onRowSelectionModelChange: actualiza selectionModel
  // - toolbar QuickFilter: búsqueda rápida global 
  // - getRowClassName: alterna estilos para filas pares/impares
  // Modales: Comments y Calibration controlados por estado
  return (
    <div className={styles.devicesContainer}>
      {rows?.length ? (
        <>
          <div style={{ width: "100%" }}>
            <DataGrid
              autoHeight
              rows={Array.isArray(rows) ? rows : []}
              columns={Array.isArray(columns) ? columns : []}
              checkboxSelection
              disableRowSelectionOnClick
              pageSizeOptions={[5, 10, 20, 50]}
              paginationModel={{ page, pageSize }}
              onPaginationModelChange={(m) => { setPage(m.page); setPageSize(m.pageSize); }}
              onRowSelectionModelChange={(sel) => setSelectionModel(sel)}
              slots={{ toolbar: GridToolbarQuickFilter }}
              slotProps={{ toolbar: { quickFilterProps: { debounceMs: 300 } } }}
              sx={{
              "& .MuiDataGrid-columnHeaders": { color: "#00ABC8", fontWeight: 600 },
              "& .MuiDataGrid-cell": { color: "#1B1464" },
              "& .MuiDataGrid-row:nth-of-type(odd)": { backgroundColor: "#FAFAFA" },
              "& .MuiDataGrid-row:nth-of-type(even)": { backgroundColor: "#D9F1F5" },
              border: 0,
            }}
            />
          </div>
        </>
      ) : (
        <div className={styles.noDevices}>
          <FormattedMessage id="no_devices">No devices assigned</FormattedMessage>
        </div>
      )}

      {/* Comments Modal */}
      <Modal open={commentsOpen} onClose={closeComments}>
        <Box sx={{ ...modalStyle, height: 500, width: "20%", overflowY: "auto" }}>
          <DevicesComments device_id={devToSeeComments} />
        </Box>
      </Modal>

      {/* Calibration Modal */}
      <Modal open={calibrationModal} onClose={closeCalibration}>
        <Box sx={{ ...modalStyle, height: !lastCalibrationInfo ? "70%" : 250, width: !lastCalibrationInfo ? "25%" : "20%" }}>
          <DeviceCalibrationModal
            deviceToCalibrate={deviceToCalibrate}
            company_id={company_id}
            onClose={closeCalibration}
            last_calibration_info={lastCalibrationInfo}
          />
        </Box>
      </Modal>

      <ToastContainer />
    </div>
  );
}
